import makeConcurrent, { byArguments as makeConcurrentByArgs } from 'make-concurrent';
import { url } from '@exodus/fetch/url';
import { conversionByRate } from '@exodus/currency';
import { SwapMode } from '../../common/types/index.js';
import { isExpired } from '../../common/utils/is-expired.js';
import { BASE_URL, DEFAULT_SLIPPAGE, STRATEGY } from './constants.js';
import checkOrderIsValidWithProvider from './verifiers/index.js';
import { getUserTimezone, pidToTxData } from './utils.js';
import { deconstructPairId } from '../../utils.js';
import { parseFetchivalError, parseFetchivalErrorWithOrder } from '../utils.js';
import { CreateOrderError, ExchangeError, ExchangeOrderError, GetProviderOrderError, MissingFromAssetError, MissingPairError, NoQuoteAvailableError, SendTxError, } from '../../errors.js';
import { LOGS } from '../../constants.js';
const DEFAULT_EXPIRY_MARGIN_MS = 10_000;
class Direct {
    swapStrategy = STRATEGY;
    sources = ['direct'];
    #appName;
    #appPlatform;
    #appVersion;
    #assetsService;
    #baseUrl;
    #fetchival;
    #options;
    #tracer;
    #assetAvailabilityService;
    #cachedRatesByPairId = new Map();
    constructor({ assetsService, assetAvailabilityService, fetchival, appName, appVersion, appPlatform, baseUrl, options = Object.create(null), tracer, }) {
        this.#baseUrl = new URL(baseUrl || BASE_URL);
        this.#appName = appName;
        this.#appPlatform = appPlatform;
        this.#appVersion = appVersion;
        this.#assetsService = assetsService;
        this.#options = options;
        this.#tracer = tracer;
        this.#fetchival = fetchival;
        this.#assetAvailabilityService = assetAvailabilityService;
    }
    async isPairSupported(pairId) {
        return this.#assetAvailabilityService.isPairSupportedBySource('direct', pairId);
    }
    async getQuote(params) {
        const { pairId, amount, swapMode = SwapMode.ExactIn, expiryMarginMs = DEFAULT_EXPIRY_MARGIN_MS, } = params;
        const { from, to } = deconstructPairId(pairId);
        const [rates, fromAsset, toAsset] = await Promise.all([
            this.#getRates(pairId, expiryMarginMs),
            this.#assetsService.getOneById(from),
            this.#assetsService.getOneById(to),
        ]);
        if (!fromAsset || !toAsset) {
            throw new MissingPairError({ pairId, fromAsset, toAsset });
        }
        const bestQuote = rates.reduce((acc, rate) => {
            const rateOutputAmount = this.#calculateAmount(fromAsset, toAsset, amount, rate.amount.value, rate.minerFee.value, swapMode);
            const isNewRateUsable = swapMode === SwapMode.ExactIn
                ? rate.min.value.lte(amount) && rate.max.value.gte(amount)
                : rate.min.value.lte(rateOutputAmount) && rate.max.value.gte(rateOutputAmount);
            if (!isNewRateUsable) {
                return acc;
            }
            if (!acc) {
                return { amount: rateOutputAmount, minerFee: rate.minerFee.value, rate };
            }
            const isNewQuoteBetter = swapMode === SwapMode.ExactIn
                ? rateOutputAmount.gt(acc.amount)
                : rateOutputAmount.lt(acc.amount);
            return isNewQuoteBetter
                ? { amount: rateOutputAmount, minerFee: rate.minerFee.value, rate }
                : acc;
        }, null);
        if (!bestQuote) {
            const { value: minValue = 'UNDEF_VAL' } = rates[0]?.min || {};
            const { value: maxValue = 'UNDEF_VAL' } = rates[0]?.max || {};
            throw new NoQuoteAvailableError({
                maxValue: maxValue === 'UNDEF_VAL' ? 'UNDEF_VAL' : maxValue.toDefaultString(),
                minValue: minValue === 'UNDEF_VAL' ? 'UNDEF_VAL' : minValue.toDefaultString(),
                inputAmount: amount.toDefaultString(),
                swapMode,
                pairId,
            });
        }
        let pidBytes;
        if (bestQuote.rate.features?.includes('pid')) {
            const randomPid = '843d630a3a7a4846615c4f6492d7135789575f3487f9b0e37dea794ffc373795';
            pidBytes = pidToTxData(randomPid);
        }
        const rate = bestQuote.rate.amount.value;
        const isExactIn = swapMode === SwapMode.ExactIn;
        const outputAmount = isExactIn
            ? toAsset.currency.defaultUnit(bestQuote.amount.toDefaultString({ unit: false }))
            : bestQuote.amount;
        return {
            fromAmount: isExactIn ? amount : outputAmount,
            toAmount: isExactIn ? outputAmount : amount,
            swapStrategy: this.swapStrategy,
            rateId: bestQuote.rate.id,
            rate,
            exchangeFeeData: {
                feeOpts: { pidBytes },
            },
            exchangeTxData: {},
            expiry: bestQuote.rate.expiry,
            min: bestQuote.rate.min.value,
            max: bestQuote.rate.max.value,
            features: [],
        };
    }
    async exchange(params) {
        let orderId;
        try {
            const orderPayload = {
                fromAddress: params.fromAddress,
                fromAddressTag: params.fromAddressTag,
                fromAmount: params.fromAmount.toDefaultString({ unit: false }),
                pairId: params.pairId,
                slippage: params.slippage || DEFAULT_SLIPPAGE,
                toAddress: params.toAddress,
                toAddressTag: params.toAddressTag,
                toAmount: params.toAmount.toDefaultString({ unit: false }),
                referralCode: params.referralCode,
                swapMode: params.swapMode,
            };
            const order = await this.#createOrder(orderPayload, params.anonymousId);
            orderId = order.id;
            const providerOrder = await this.#getProviderOrder(order.id);
            if (!this.#options.bypassProvidersValidation) {
                await this.#checkOrderIsValid(order, providerOrder);
            }
            const { from } = deconstructPairId(params.pairId);
            const [fromAsset, opts] = await Promise.all([
                this.#assetsService.getOneById(from),
                this.#getExchangeOpts(order),
            ]);
            if (!fromAsset) {
                throw new MissingFromAssetError({ fromAsset: from, orderId: order.id });
            }
            const transactionAmount = params.swapMode === 'ExactIn'
                ? params.fromAmount
                : fromAsset.currency.defaultUnit(order.amount.value);
            const txId = await this.#sendTransaction({
                amount: transactionAmount,
                payInAddress: order.payInAddress,
                fromAsset,
                opts,
                isSendAll: params.isSendAll,
                quote: params.quote,
                createAndSendTx: params.createAndSendTx,
            });
            void this.#updateOrder(order.id, txId);
            return {
                transactions: [{ type: 'depositTransaction', id: txId }],
                svc: providerOrder.provider.slug,
                providerOrderId: providerOrder.id,
                orderId: order.id,
            };
        }
        catch (err) {
            if (!err.orderId && orderId) {
                err.orderId = orderId;
            }
            if (err instanceof ExchangeError) {
                throw err;
            }
            throw new ExchangeError({ message: err.message, orderId }, { cause: err });
        }
    }
    getLimits = makeConcurrent(async ({ pairId, expiryMarginMs = DEFAULT_EXPIRY_MARGIN_MS, }) => {
        const rates = await this.#getRates(pairId, expiryMarginMs);
        const { from, to } = deconstructPairId(pairId);
        const [fromAsset, toAsset] = await Promise.all([
            this.#assetsService.getOneById(from),
            this.#assetsService.getOneById(to),
        ]);
        if (!fromAsset || !toAsset) {
            throw new MissingPairError({ pairId, fromAsset, toAsset });
        }
        return rates.map(({ amount, min, max, minerFee, expiry }) => {
            const rateDecimals = this.#getDecimals(amount.value);
            const exactOutMin = rateDecimals > toAsset.decimals || toAsset.decimals > fromAsset.decimals
                ? fromAsset.currency.defaultUnit(this.#changeSmallestDecimal(min.value, fromAsset, 'increment'))
                : min.value;
            const exactOutMax = rateDecimals > toAsset.decimals
                ? fromAsset.currency.defaultUnit(this.#changeSmallestDecimal(max.value, fromAsset, 'decrement'))
                : max.value;
            const exactOutMinAmount = this.#calculateAmount(fromAsset, toAsset, fromAsset.currency.defaultUnit(exactOutMin), amount.value, minerFee.value, SwapMode.ExactIn);
            const exactOutMaxAmount = this.#calculateAmount(fromAsset, toAsset, fromAsset.currency.defaultUnit(exactOutMax), amount.value, minerFee.value, SwapMode.ExactIn);
            return {
                [SwapMode.ExactIn]: {
                    min: min.value,
                    max: max.value,
                },
                [SwapMode.ExactOut]: {
                    min: exactOutMinAmount,
                    max: exactOutMaxAmount,
                },
                expiry,
            };
        });
    });
    setGeolocation(_geolocation) { }
    setTransactionPrivacyNetworks(_transactionPrivacyNetworks) { }
    #getRates = makeConcurrentByArgs(async (pairId, expiryMarginMs) => {
        const cachedRates = this.#cachedRatesByPairId.get(pairId);
        const shouldFetch = !cachedRates || isExpired(cachedRates.expiry, expiryMarginMs);
        if (shouldFetch) {
            const rates = await this.#getRatesFromServer(pairId);
            if (rates.length === 0) {
                return [];
            }
            const expiry = rates.reduce((acc, rate) => Math.min(acc, rate.expiry), rates[0].expiry);
            this.#cachedRatesByPairId.set(pairId, { rates, expiry });
        }
        return this.#cachedRatesByPairId.get(pairId).rates;
    }, ([pairId, expiryMarginMs]) => `${pairId}:${expiryMarginMs}`);
    #getRatesFromServer = async (pairId) => {
        const rates = await this.#request(url `${this.#baseUrl}/pairs/${pairId}/rates`).get({
            ...(this.#options.extraFeatures ? { extraFeatures: this.#options.extraFeatures } : {}),
        });
        const { from, to } = deconstructPairId(pairId);
        const [fromAsset, toAsset] = await Promise.all([
            this.#assetsService.getOneById(from),
            this.#assetsService.getOneById(to),
        ]);
        if (!fromAsset || !toAsset) {
            throw new MissingPairError({ pairId, fromAsset, toAsset });
        }
        return rates.map((rate) => {
            const min = fromAsset.currency.defaultUnit(rate.min.value);
            const max = fromAsset.currency.defaultUnit(rate.max.value);
            const minerFee = toAsset.currency.defaultUnit(rate.minerFee.value);
            return {
                ...rate,
                amount: {
                    ...rate.amount,
                    value: rate.amount.value.toString(),
                },
                min: {
                    ...rate.min,
                    value: min,
                },
                max: {
                    ...rate.max,
                    value: max,
                },
                minerFee: {
                    ...rate.minerFee,
                    value: minerFee,
                },
            };
        });
    };
    #request(url, { anonymousId, userTimezone } = {}) {
        return this.#fetchival(url, {
            headers: {
                'App-Name': this.#appName,
                'App-Version': this.#appVersion,
                ...(this.#appPlatform ? { 'App-Platform': this.#appPlatform } : {}),
                ...(anonymousId ? { anonymousId } : {}),
                ...(userTimezone ? { 'User-Timezone': userTimezone } : {}),
            },
        });
    }
    #getExchangeOpts = async (order) => {
        this.#tracer?.traceTime('GetExchangeOpts');
        const opts = {};
        if (order.extraFeatures?.pid) {
            opts.pidBytes = pidToTxData(order.extraFeatures.pid);
        }
        if (order.payInAddressTag) {
            opts.payInAddressTag = order.payInAddressTag;
        }
        this.#tracer?.traceTimeEnd('GetExchangeOpts');
        return opts;
    };
    #traceMessage(message) {
        this.#tracer?.traceMessage(message);
    }
    #calculateAmount(fromAsset, toAsset, amount, rateAmount, minerFee, swapMode) {
        if (swapMode === SwapMode.ExactIn) {
            return this.#calculateToAmount(fromAsset, toAsset, amount, rateAmount, minerFee);
        }
        return this.#calculateFromAmount(fromAsset, toAsset, amount, rateAmount, minerFee);
    }
    #calculateToAmount(fromAsset, toAsset, amount, rateAmount, minerFee) {
        const convert = conversionByRate(fromAsset.currency, toAsset.currency, rateAmount);
        const convertedAmount = convert(amount);
        return convertedAmount.sub(toAsset.currency.defaultUnit(minerFee.toDefaultString()));
    }
    #calculateFromAmount(fromAsset, toAsset, amount, rateAmount, minerFee) {
        const rateAmountReversed = String(1 / parseFloat(rateAmount));
        const amounPlusMinerFee = amount.add(toAsset.currency.defaultUnit(minerFee.toDefaultString()));
        const convert = conversionByRate(toAsset.currency, fromAsset.currency, rateAmountReversed);
        return convert(amounPlusMinerFee);
    }
    #getDecimals = (amount) => {
        if (!amount.includes('.'))
            return 0;
        return amount.split('.')[1].length;
    };
    #changeSmallestDecimal = (amount, fromAsset, change) => {
        const decimals = this.#getDecimals(amount.toDefaultString());
        const decimalAmount = fromAsset.currency.defaultUnit(1 / 10 ** decimals);
        if (change === 'increment') {
            return amount.add(decimalAmount);
        }
        return amount.sub(decimalAmount);
    };
    async #createOrder(orderPayload, anonymousId) {
        this.#traceMessage(LOGS.CREATING_ORDER);
        return this.#withTimeTracker('CreateOrder', async () => {
            try {
                return await this.#request(new URL(`${this.#baseUrl}/orders?${new URLSearchParams({
                    extraFeatures: [...(this.#options.extraFeatures || []), 'stringAmounts'].join(','),
                }).toString()}`), { anonymousId, userTimezone: getUserTimezone() }).post(orderPayload);
            }
            catch (err) {
                const { message, orderId } = await parseFetchivalErrorWithOrder(err);
                this.#traceMessage(LOGS.FAILED_TO_CREATE_ORDER);
                throw new CreateOrderError({
                    message,
                    details: orderPayload,
                    orderId,
                }, { cause: err });
            }
        });
    }
    async #getProviderOrder(orderId) {
        this.#traceMessage(LOGS.GETTING_PROVIDER_ORDER);
        return this.#withTimeTracker('GetProviderOrder', async () => {
            try {
                const providerOrder = await this.#request(url `${this.#baseUrl}/orders/${orderId}/provider-order`).get();
                if (!providerOrder) {
                    throw new ExchangeOrderError({
                        message: `Unable to get order ${orderId} from provider`,
                        orderId,
                    });
                }
                return providerOrder;
            }
            catch (err) {
                this.#traceMessage(LOGS.FAILED_TO_GET_TX);
                const errorMessage = await parseFetchivalError(err);
                throw new GetProviderOrderError({ message: errorMessage, orderId }, { cause: err });
            }
        });
    }
    async #checkOrderIsValid(order, providerOrder) {
        this.#traceMessage(LOGS.CHECKING_ORDER_VALIDITY);
        return this.#withTimeTracker('CheckOrderIsValid', async () => {
            try {
                await checkOrderIsValidWithProvider({
                    order,
                    providerOrder,
                    fetchival: this.#fetchival,
                });
            }
            catch (err) {
                this.#traceMessage(LOGS.FAILED_TO_VALIDATE_ORDER);
                throw err;
            }
        });
    }
    async #sendTransaction(params) {
        const { amount, payInAddress, fromAsset, opts, isSendAll, quote, createAndSendTx } = params;
        this.#traceMessage(LOGS.SENDING_TX);
        const txPayload = {
            assetId: fromAsset.id,
            amount,
            recipient: payInAddress,
            opts,
            isSendAll,
            feeOpts: quote.exchangeFeeData.feeOpts,
            quote,
        };
        return this.#withTimeTracker('SendTx', async () => {
            try {
                const { txId } = await createAndSendTx(txPayload);
                return txId;
            }
            catch (err) {
                this.#traceMessage(LOGS.FAILED_TO_SEND_TX);
                throw new SendTxError({
                    message: err.message,
                    details: txPayload,
                }, { cause: err });
            }
        });
    }
    async #updateOrder(orderId, txId) {
        this.#traceMessage(LOGS.UPDATING_ORDER);
        return this.#withTimeTracker('UpdateOrder', async () => {
            await this.#request(url `${this.#baseUrl}/orders/${orderId}`)
                .patch({ transactionId: txId })
                .catch((err) => console.warn(`Fail to update order ${orderId} with txId ${txId}`, err));
        });
    }
    async #withTimeTracker(eventName, fn) {
        try {
            this.#tracer?.traceTime(eventName);
            return await fn();
        }
        finally {
            this.#tracer?.traceTimeEnd(eventName);
        }
    }
}
export default Direct;
